import { useLayoutEffect, useRef } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { useSitePromoBanner } from '@opendoor/shared-fe/sitePromoBanner';
import {
  type SitePromoBannerCampaign,
  sitePromoBannerJourneyTarget,
} from '@opendoor/shared-ts/site-promo-banner';
import { SITE_PROMO_BANNER } from '@opendoor/shared-ts/site-promo-banner-config';
import { IconArrowRight, IconX } from '@tabler/icons-react';

import { COSMOS_URL } from '../globals';

const HEIGHT_PROPERTY = '--site-promo-banner-height';
const VISIBLE_HEIGHT_PROPERTY = '--site-promo-banner-visible-height';

export function SitePromoBanner({ pathname }: { pathname: string }) {
  const { campaign, dismiss } = useSitePromoBanner({ campaign: SITE_PROMO_BANNER, pathname });
  if (!campaign) return null;
  return <SitePromoBannerContent campaign={campaign} dismiss={dismiss} />;
}

export function SitePromoBannerContent({
  campaign,
  dismiss,
}: {
  campaign: SitePromoBannerCampaign;
  dismiss: () => void;
}) {
  const bannerRef = useRef<HTMLElement | null>(null);
  const journeyTarget = sitePromoBannerJourneyTarget(campaign.id);
  const brand = campaign.variant === 'brand';
  const buttonLink = campaign.linkStyle === 'button';
  const href = campaign.link.href.startsWith('/')
    ? `${COSMOS_URL}${campaign.link.href}`
    : campaign.link.href;

  useLayoutEffect(() => {
    const banner = bannerRef.current;
    if (!banner) return undefined;
    const updateHeight = () => {
      const height = banner.offsetHeight;
      const visibleHeight = Math.max(0, Math.min(height, banner.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty(HEIGHT_PROPERTY, `${height}px`);
      document.documentElement.style.setProperty(VISIBLE_HEIGHT_PROPERTY, `${visibleHeight}px`);
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(banner);
    window.addEventListener('scroll', updateHeight, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateHeight);
      document.documentElement.style.removeProperty(HEIGHT_PROPERTY);
      document.documentElement.style.removeProperty(VISIBLE_HEIGHT_PROPERTY);
    };
  }, []);

  return (
    <Box
      as="aside"
      ref={bannerRef}
      analyticsName={journeyTarget}
      trackImpression
      aria-label="Promotion"
      className={`relative z-[2001] block ${brand ? 'bg-od-bg-accent-brand text-od-content-inverse-primary' : 'border-b border-od-border-primary bg-site-promo-quiet text-od-content-primary'}`}
      data-site-promo-banner
    >
      <Row className="min-h-11 flex-wrap items-center justify-center gap-x-2 px-12 py-1 text-center text-body-md sm:px-14">
        <Text as="span" className="min-w-0 max-w-full break-words sm:hidden">
          {campaign.mobile.message}
        </Text>
        <Text as="span" className="hidden min-w-0 max-w-full break-words sm:inline">
          {campaign.message}
        </Text>
        <Link
          analyticsName={journeyTarget}
          aria-label={buttonLink ? campaign.link.label : undefined}
          className={`inline-flex shrink-0 items-center focus-visible:outline-2 focus-visible:outline-offset-2 ${
            buttonLink
              ? `group size-11 justify-center !no-underline ${brand ? '!text-od-content-accent-brand focus-visible:outline-white' : '!text-od-content-inverse-primary focus-visible:outline-od-content-primary'}`
              : `min-h-11 gap-1 whitespace-nowrap underline underline-offset-2 hover:no-underline ${brand ? '!text-od-content-inverse-primary' : 'text-od-content-primary'}`
          }`}
          href={href}
          // Beacon-owned destinations need a document navigation through the public router.
          // oxlint-disable-next-line jsx-a11y/anchor-is-valid -- Link merges in the href
          render={<a aria-label={buttonLink ? campaign.link.label : undefined} />}
        >
          {!buttonLink && (
            <>
              <Text as="span" className="sm:hidden">
                {campaign.mobile.linkLabel}
              </Text>
              <Text as="span" className="hidden sm:inline">
                {campaign.link.label}
              </Text>
            </>
          )}
          {buttonLink ? (
            <Text
              as="span"
              className={`flex size-6 items-center justify-center rounded-lg transition-opacity group-hover:opacity-90 ${brand ? 'bg-od-bg-primary' : 'bg-od-bg-inverse-primary'}`}
            >
              <IconArrowRight aria-hidden size={14} stroke={2.25} />
            </Text>
          ) : (
            <IconArrowRight aria-hidden size={16} stroke={2} />
          )}
        </Link>
      </Row>
      <Button
        aria-label="Dismiss promotion"
        analyticsName="cosmos-site-promo-banner-dismiss"
        className={`absolute right-1 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-transparent p-0 focus-visible:outline-2 focus-visible:outline-offset-2 sm:right-2 ${brand ? '!text-od-content-inverse-primary hover:!bg-white/15' : '!text-od-content-primary not-disabled:hover:bg-od-content-primary/10'}`}
        onClick={dismiss}
        size="md"
        type="button"
        variant={brand ? 'secondaryInverse' : 'secondary'}
      >
        <IconX aria-hidden size={16} stroke={1.5} />
      </Button>
    </Box>
  );
}
